import React, { memo } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { trackLead } from '@/lib/metaPixel';

const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";

const faqs = [
  {
    question: "Will this work for my genre?",
    answer: "Short answer: yes, if there is an audience for it.\n\nWe work across pop, hip-hop, indie, rock, ambient, country, electronic, and everything in between. The reason our results translate across genres is because we are not relying on trends or playlists alone. We focus on audience behaviour, storytelling, content hooks and release strategy. Those fundamentals work in every genre.\n\nIf people already listen to artists like you, we can build a system that helps them find you."
  },
  {
    question: "How long does it take to see results?",
    answer: "You will usually see early signals within the first 30 days.\n\nThis might look like increased saves, higher listener retention, content traction or clearer audience data. Bigger outcomes like sustained stream growth, algorithmic support and fan conversion typically build over 60 to 90 days.\n\nThis is not a one-post or one-song tactic. We build momentum that compounds."
  },
  {
    question: "Do I need a big following already?",
    answer: "No. You need good music and the willingness to execute.\n\nSome of our strongest case studies started with zero, small or inconsistent audiences. What matters more than follower count is having releasable music and being open to a clear strategy.\n\nIf you already have momentum, we can accelerate it. If you are earlier, we build the foundation properly so growth actually sticks."
  },
  {
    question: "Do I have to be a 'content creator' to make this work?",
    answer: "No. You don't need to be an influencer, you just need to understand why content matters.\n\nWe know social media can feel time-consuming, uncomfortable, and sometimes at odds with why you make music in the first place.\n\nBut content isn't the goal, it's the tool. It's how your music gets discovered, contextualised, and taken seriously by fans, industry, and opportunities.\n\nWe focus on documenting your music, your process, and your story in a way that feels aligned with who you are as an artist - so you can build visibility, momentum, and credibility without pretending to be something you're not.\n\nIn short: you make the music. We help make sure the right people actually hear it."
  },
  {
    question: "How do you measure success?",
    answer: "This will look different for each artist, since we all have different goals. But we don't measure success by one viral post or a short-term spike in streams. Instead, we look for clear signals of real momentum, such as:\n\n• Growing monthly listeners\n• Consistent engagement\n• A strong, functioning ecosystem around you and your music\n• Actual connection with an audience (not just bots)\n\nFor us, it's ultimately about building something that supports long-term growth."
  },
  {
    question: "What makes you different and why should I trust you?",
    answer: "Honestly, trust is difficult online. We get that.\n\nThe music industry is full of big promises, rented screenshots and tactics that stop working the moment you stop paying. That is exactly why we have built this business the way we have.\n\nDamian co-founded BIMM, now the largest music university in Europe, long before social media marketing was even a thing. Our work has always been rooted in artist development, not shortcuts. Over the years, we have helped thousands of artists through our agency, education platforms and content, making us one of the largest and most visible music marketing teams online.\n\nWe are not perfect, but we are good people doing our best, showing our work publicly, and building long-term careers with artists rather than chasing quick wins.\n\nWhat makes us 'different' is that if you work with us, you are working with a team that actually cares about your music, your growth and your future."
  }
];

const CaseStudiesFAQComponent = () => {
  return (
    <section className="pt-10 lg:pt-16 pb-20 lg:pb-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Dark rounded container */}
        <div 
          className="relative overflow-hidden border border-white/10 rounded-[32px] px-4 py-16 lg:px-12 lg:py-20 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
          data-nav-theme="dark"
          style={{
            background: 'radial-gradient(ellipse at center, hsl(0, 0%, 7%) 0%, hsl(0, 0%, 3%) 70%)'
          }}
        >

          <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          {/* Badge */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
              <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                &gt; FAQ
              </span>
            </div>
          </div>

          <h2 className="headline-lg text-white text-center mx-auto mb-3">
            Frequently Asked{' '}
            <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
              Questions
            </span>
          </h2>
          <p className="body-large text-white/60 text-center max-w-xl mx-auto">
            Everything you need to know about working together
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="w-full space-y-3 mb-10">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="rounded-lg px-5 bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.12] transition-colors"
            >
              <AccordionTrigger className="text-left text-white/90 hover:text-white transition-colors text-base py-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-white/60 text-sm leading-relaxed pb-4 whitespace-pre-line">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* CTA */}
        <div className="text-center">
          <p className="text-white/50 text-sm mb-5">
            Ready to see what's possible?
          </p>
          <div className="inline-block group shadow-[0_0_16px_rgba(0,113,227,0.20)] md:shadow-[0_0_30px_rgba(0,113,227,0.35)] md:rounded-xl">
            <Button 
              variant="hero-black-ring" 
              size="hero" 
              className="w-auto relative border border-[#0071e3]/40 shadow-[0_0_14px_rgba(0,113,227,0.18)] hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.35)] transition-all duration-300 hover:scale-[1.02]"
              asChild
            >
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead} className="inline-flex items-center gap-2">
                Apply Now
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Button>
          </div>
        </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export const CaseStudiesFAQ = memo(CaseStudiesFAQComponent);
export default CaseStudiesFAQ;
